import {createSlice, PayloadAction} from '@reduxjs/toolkit';

export interface BatchPrintState {
  saveBatchPrint: any;
  selectedSource: string;
  selectedSourceActionType: string;
  expandedParentIds: string[];
  basisForPrinting: any;
  analysis: {
    printTotalDocCount: number;
    noIssuesDocCount: number;
    notAnyProductionDocCount: number;
    moreProducitonDocCount: number;
    skipFolderToggle?: boolean;
    selectedSkippedFolder?: string;
    decisionDocCount?: number;
    decisions?: Record<
      string,
      {
        action: 'SKIP' | 'PRODUCTION';
        productionKey?: string;
      }
    >;
    translationAction: string;
    translationLanguage: any;
    translationSkipOrIgnoreDocuments: any[];
  };
  brandingDetails: {
    Location: number;
    Html: string;
  }[];
  includeRedactions: boolean;
  shouldBurnTransparentRedactions: boolean;
  includeTableOfContent: boolean;
  exceptionFileTypes: {
    excelFilesDocCount: number;
    mediaFilesDocCount: number;
    otherExceptionFileTypes: number;
  };
}

const initialState: BatchPrintState = {
  analysis: {
    printTotalDocCount: 0,
    noIssuesDocCount: 0,
    notAnyProductionDocCount: 0,
    moreProducitonDocCount: 0,
    skipFolderToggle: true,
    decisions: {},
    selectedSkippedFolder: '',
    decisionDocCount: 0,
    translationAction: 'natives',
    translationLanguage: {id: 1, name: 'English - United States'},
    translationSkipOrIgnoreDocuments: [],
  },
  selectedSource: '',
  selectedSourceActionType: '',
  expandedParentIds: [],
  basisForPrinting: {
    printIssueSettingCode: 'SKIPPRINTBUTFOLDER',
    selectedProductionSet: {
      id: '',
      name: 'Select',
    },
    selectedProductionList: [
      {
        id: '',
        label: '',
      },
    ],
  },
  saveBatchPrint: {
    batchPrintDTO: {
      projectID: 0,
      userID: 0,
      securityGroupID: 0,
      roleID: 0,
      emailID: '',
      batchPrintID: 0,
      projectGUID: '00000000-0000-0000-0000-000000000000',
      id: 0,
      sourceContextTypeCode: '',
      sourceContextTypeID: 0,
      searchID: 0,
      tagID: 0,
      folderID: 0,
      isProductionPrint: true,
      productionSetID: 0,
      productionIDs: [],
      printIssueSettingCode: 'SKIPPRINTBUTFOLDER',
      translationLanguage: '',
      folderIDForIssueSkippedDocuments: 0,
      issueDocuments: [],
      includeMediaFilePlaceHolder: false,
      mediaFilePlaceHolderHtml: '',
      isExcelFileSkipped: false,
      includeExcelFilePlaceHolder: false,
      excelFilePlaceHolderHtml: '',
      includeOtherExceptionFileTypesPlaceHolder: false,
      otherExceptionFileTypesPlaceHolderHtml: '',
      includeSlipSheet: true,
      isNewSlipSheet: false,
      slipSheetDetails: [],
      includeCoverIntroPage: false,
      includeTableOfContent: false,
      includeKeywordConceptHighlights: false,
      displayTOCNumberInSeq: true,
      coverSheetRecipientName: '',
      coverSheetSenderName: '',
      coverSheetCarbonCopyName: '',
      coverSheetDescription: '',
      coverSheetTitle: '',
      coverSheetDate: '',
      savedSearchSelection: '',
      includeRedactions: true,
      shouldBurnTransparentRedactions: true,
      brandingDetails: [],
      exportFileNameFieldID: 0,
      exportFileNameFieldName: '',
      sortByFieldID: 0,
      sortByFieldName: '',
      sortOrder: '',
      shouldCreateAllInOnePDF: false,
      printedFilePaths: [],
      notificationURL: '',
      maxPagesPerFile: 0,
      workspacePath: '',
      outputPath: '',
      batchPrintStatusErrorType: 0,
    },
  },
  brandingDetails: [],
  includeRedactions: true,
  shouldBurnTransparentRedactions: false,
  includeTableOfContent: false,
  exceptionFileTypes: {
    excelFilesDocCount: 0,
    mediaFilesDocCount: 0,
    otherExceptionFileTypes: 0,
  },
};

export const batchPrintSlice = createSlice({
  name: 'batchPrint',
  initialState,
  reducers: {
    setSelectedSource: (state, action: PayloadAction<string>) => {
      state.selectedSource = action.payload;
    },
    setSelectedSourceActionType: (state, action: PayloadAction<string>) => {
      state.selectedSourceActionType = action.payload;
    },
    setExpandedParentIds: (state, action: PayloadAction<string[]>) => {
      state.expandedParentIds = action.payload;
    },
    setPrintIssueSettingCode: (state, action: PayloadAction<any>) => {
      state.basisForPrinting.printIssueSettingCode = action.payload;
    },
    setSelectedProductionSet: (state, action: PayloadAction<any>) => {
      state.basisForPrinting.selectedProductionSet = action.payload;
    },
    setSelectedProductionList: (state, action: PayloadAction<any>) => {
      state.basisForPrinting.selectedProductionList = action.payload;
    },
    setAnalysis: (state, action: PayloadAction<any>) => {
      state.analysis = action.payload;
    },
    setAnalysisSkipFolderToggle: (state, action: PayloadAction<boolean>) => {
      state.analysis.skipFolderToggle = action.payload;
    },
    setAnalysisSelectedSkipFolder: (state, action: PayloadAction<string>) => {
      state.analysis.selectedSkippedFolder = action.payload;
    },
    setSaveBatchPrint: (state, action: PayloadAction<any>) => {
      state.saveBatchPrint.batchPrintDTO = {
        ...state.saveBatchPrint.batchPrintDTO,
        ...action.payload,
      };
    },
    setAnalysisDecision: (
      state,
      action: PayloadAction<{
        docId: string;
        action: 'SKIP' | 'PRODUCTION';
        productionKey?: string;
      }>
    ) => {
      if (!state.analysis.decisions) {
        state.analysis.decisions = {};
      }

      state.analysis.decisions[action.payload.docId] = {
        action: action.payload.action,
        productionKey: action.payload.productionKey,
      };
    },
    setAnalysisTranslationAction: (state, action: PayloadAction<string>) => {
      state.analysis.translationAction = action.payload;
    },
    setAnalysisTranslationLanguage: (state, action: PayloadAction<any>) => {
      state.analysis.translationLanguage = action.payload;
      state.saveBatchPrint.batchPrintDTO.translationLanguage = action.payload;
    },
    setAnalysisTranslationSkipOrIgnoreDocuments: (
      state,
      action: PayloadAction<any>
    ) => {
      state.analysis.translationSkipOrIgnoreDocuments = action.payload;
    },
    setIsIncludeRedactions: (state, action: PayloadAction<boolean>) => {
      state.includeRedactions = action.payload;
      state.saveBatchPrint.batchPrintDTO.includeRedactions = action.payload;
    },
    setShouldBurnTransparentRedactions: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.shouldBurnTransparentRedactions = action.payload;
      state.saveBatchPrint.batchPrintDTO.batchPrintDTO = action.payload;
    },
    setBrandingDetails: (
      state,
      action: PayloadAction<
        {
          Location: number;
          Html: string;
        }[]
      >
    ) => {
      state.brandingDetails = action.payload;
      state.saveBatchPrint.batchPrintDTO.brandingDetails = action.payload;
    },
    setExceptionFileTypes: (
      state,
      action: PayloadAction<{
        excelFilesDocCount: number;
        mediaFilesDocCount: number;
        otherExceptionFileTypes: number;
      }>
    ) => {
      state.exceptionFileTypes = action.payload;
    },
    resetBatchPrintData: () => initialState,
  },
});

export const {
  setSelectedSource,
  setSelectedSourceActionType,
  setExpandedParentIds,
  setSaveBatchPrint,
  setPrintIssueSettingCode,
  setSelectedProductionSet,
  setSelectedProductionList,
  setAnalysis,
  setAnalysisSkipFolderToggle,
  setAnalysisDecision,
  setAnalysisSelectedSkipFolder,
  setAnalysisTranslationAction,
  setAnalysisTranslationLanguage,
  setAnalysisTranslationSkipOrIgnoreDocuments,
  setIsIncludeRedactions,
  setShouldBurnTransparentRedactions,
  setBrandingDetails,
  setExceptionFileTypes,
  resetBatchPrintData,
} = batchPrintSlice.actions;

export default batchPrintSlice.reducer;
